import React from 'react';

/**
 * Jira-style lozenge. Small, uppercase, semantic colors. Use this
 * everywhere you'd previously have rendered an inline colored chip
 * (epic state, archive flag, repo override hint, etc.).
 */
export type StatusTone =
  | 'neutral'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger'
  | 'purple'
  | 'inprogress';

// Light classes rely on the light tint (bg-*-50) + dark text (text-*-700).
// In dark mode the tint inverts to a deep shade while the mid/fill shades are
// preserved, so a pale-tint + kept-shade pairing can lose contrast. Each tone
// therefore pins an explicit dark treatment: a translucent saturated fill for
// the semantic hue + a near-white label that stays legible on the dark card.
const TONE: Record<StatusTone, string> = {
  neutral: 'bg-slate-100 text-slate-700 border-slate-200 dark:text-slate-800',
  info: 'bg-primary-50 text-primary-700 border-primary-100 dark:bg-primary-500/20 dark:text-[#EDF1F7] dark:border-primary-400/30',
  success: 'bg-success-50 text-success-700 border-success-100 dark:bg-success-500/20 dark:text-[#EDF1F7] dark:border-success-500/30',
  warning: 'bg-warning-50 text-warning-700 border-warning-100 dark:bg-warning-500/25 dark:text-[#EDF1F7] dark:border-warning-500/30',
  danger: 'bg-danger-50 text-danger-700 border-danger-100 dark:bg-danger-500/25 dark:text-[#EDF1F7] dark:border-danger-500/30',
  purple: 'bg-purple-50 text-purple-600 border-purple-50 dark:bg-purple-500/25 dark:text-[#EDF1F7] dark:border-purple-500/30',
  inprogress: 'bg-accent-50 text-accent-600 border-accent-50 dark:bg-accent-500/25 dark:text-[#EDF1F7] dark:border-accent-500/30',
};

interface StatusBadgeProps {
  tone?: StatusTone;
  /** Render with a tiny dot before the label (Jira "STATUS" style). */
  dot?: boolean;
  className?: string;
  children: React.ReactNode;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  tone = 'neutral',
  dot,
  className = '',
  children,
}) => (
  <span
    className={
      'inline-flex items-center gap-1 px-2 py-0.5 text-2xs font-semibold ' +
      'uppercase tracking-wide rounded border ' +
      TONE[tone] + ' ' + className
    }
  >
    {dot && (
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
    )}
    {children}
  </span>
);

/**
 * Map a raw epic state string to a StatusBadge tone. Falls back to
 * neutral for unknown values so we never crash on a new orchestrator state.
 */
export function toneForEpicState(state: string): StatusTone {
  switch (state) {
    case 'COMPLETE':
    case 'COMPLETED':                 // M3: cross-repo emits COMPLETED (was rendering gray)
    case 'READY_FOR_REVIEW':
    case 'READY_FOR_REVIEW_NOTIFIED':
    case 'BUGFIX_COMPLETE':
      return 'success';
    case 'FAILED':
    case 'ABANDONED':
    case 'ABORTED':
    case 'ROLLED_BACK':               // M3: terminal rollback outcome
    case 'INIT_FAILED':               // brownfield deep-init: launch/agent failure
      return 'danger';
    case 'IN_PROGRESS':
    case 'BUILDING':
    case 'PLANNING':                  // M3
    case 'GRAPH_REFRESHING':          // M3
    case 'EPIC_VALIDATING':           // M3
    case 'READY_FOR_DEPLOY':          // M3
    case 'DEPLOYING_DEV':
    case 'TESTING_DEV':
    case 'DEPLOYING_TEST':
    case 'TESTING_TEST':
    case 'ROLLBACK_IN_PROGRESS':      // M3: in-flight rollback
    case 'REQUIREMENTS_QUESTIONS_IN_PROGRESS': // M3
    case 'PLAN_QUESTIONS_IN_PROGRESS':         // M3
    case 'BUGFIX_IN_PROGRESS':
    case 'INIT_RUNNING':              // brownfield deep-init
    case 'INIT_GRAPH_REFRESHING':
    case 'INIT_ANALYZING':
    case 'INIT_QUESTIONS_IN_PROGRESS':
      return 'inprogress';
    case 'PLAN_POSTED':
    case 'DEFINED':
    case 'TARGETS_PENDING':           // M3: human-wait gates
    case 'PLAN_QUESTIONS':            // M3
    case 'REQUIREMENTS_REVIEW':       // M3
    case 'REQUIREMENTS_ALIGNED':      // M3
    case 'REQUIREMENTS_QUESTIONS':    // M3
    case 'REQUIREMENTS_REPLY_RECEIVED': // M3
    case 'BUGFIX_DEFINED':
    case 'INIT_DEFINED':              // brownfield deep-init: queued, not yet launched
    case 'INIT_SCANNED':
    case 'INIT_QUESTIONS':            // waiting on a human reply (intake Q&A)
    case 'INIT_REPLY_RECEIVED':
    case 'INIT_ENRICHED':
      return 'info';
    case 'INIT_COMPLETE':             // brownfield deep-init: terminal success
      return 'success';
    default:
      return 'neutral';
  }
}

/**
 * Friendlier display label for the operator. Long uppercase Forge state
 * strings get condensed to scannable phrases.
 */
export function labelForEpicState(state: string): string {
  switch (state) {
    case 'BUGFIX_DEFINED': return 'Queued';
    case 'BUGFIX_IN_PROGRESS': return 'Fixing…';
    case 'BUGFIX_COMPLETE': return 'Fix complete';
    case 'READY_FOR_REVIEW':
    case 'READY_FOR_REVIEW_NOTIFIED':
      return 'PR Ready';
    case 'ABORTED': return 'Aborted';
    // M3 (2026-07-14): condense the FORGE-306/313 gate + deploy/rollback states.
    case 'COMPLETED': return 'Complete';
    case 'GRAPH_REFRESHING': return 'Refreshing graph…';
    case 'REQUIREMENTS_REVIEW': return 'Reviewing requirements…';
    case 'REQUIREMENTS_ALIGNED': return 'Requirements aligned';
    case 'REQUIREMENTS_QUESTIONS': return 'Needs answers';
    case 'REQUIREMENTS_QUESTIONS_IN_PROGRESS': return 'Answering…';
    case 'REQUIREMENTS_REPLY_RECEIVED': return 'Reply received';
    case 'TARGETS_PENDING': return 'Confirm targets';
    case 'PLANNING': return 'Planning…';
    case 'PLAN_QUESTIONS': return 'Needs answers';
    case 'PLAN_QUESTIONS_IN_PROGRESS': return 'Answering…';
    case 'EPIC_VALIDATING': return 'Validating…';
    case 'READY_FOR_DEPLOY': return 'Ready to deploy';
    case 'ROLLBACK_IN_PROGRESS': return 'Rolling back…';
    case 'ROLLED_BACK': return 'Rolled back';
    // Brownfield deep-init (/init) — pseudo-epic #0, kind:'init'.
    case 'INIT_DEFINED': return 'Queued';
    case 'INIT_RUNNING': return 'Scanning repo…';
    case 'INIT_SCANNED': return 'Scanned';
    case 'INIT_GRAPH_REFRESHING': return 'Refreshing graph…';
    case 'INIT_ANALYZING': return 'Analyzing…';
    case 'INIT_QUESTIONS': return 'Needs answers';
    case 'INIT_REPLY_RECEIVED': return 'Reply received';
    case 'INIT_QUESTIONS_IN_PROGRESS': return 'Answering…';
    case 'INIT_ENRICHED': return 'Enriching solution.md…';
    case 'INIT_COMPLETE': return 'Init complete';
    case 'INIT_FAILED': return 'Init failed';
    default: return state;
  }
}

/**
 * FORGE-302 — map a PRWatchState to a StatusBadge tone. Mirrors
 * toneForEpicState's fallback-to-neutral discipline for forward compat.
 */
export function toneForPRWatchState(state: string): StatusTone {
  switch (state) {
    case 'PR_MERGED':
      return 'success';
    case 'PR_CLOSED':
      return 'neutral';
    case 'PR_FIX_ESCALATED':
    case 'RESOLUTION_READY': // FORGE-309: awaiting the human's Approve/Reject
      return 'warning';
    case 'FIXING':
    case 'FEEDBACK_QUEUED':
    case 'PROPOSING_RESOLUTION': // FORGE-309: proposer drafting
    case 'RESOLUTION_REVIEW':    // FORGE-309: reviewer gating
      return 'inprogress';
    case 'WATCHING':
      return 'info';
    default:
      return 'neutral';
  }
}

/** Friendlier display label for a PRWatchState. */
export function labelForPRWatchState(state: string): string {
  switch (state) {
    case 'WATCHING': return 'Watching';
    case 'FEEDBACK_QUEUED': return 'Feedback queued';
    case 'FIXING': return 'Fixing…';
    case 'PROPOSING_RESOLUTION': return 'Drafting fix…';
    case 'RESOLUTION_REVIEW': return 'Reviewing draft…';
    case 'RESOLUTION_READY': return 'Approve resolution';
    case 'PR_FIX_ESCALATED': return 'Needs human';
    case 'PR_MERGED': return 'Merged';
    case 'PR_CLOSED': return 'Closed';
    default: return state;
  }
}

/**
 * FORGE-303 — map a per-story confidence grade to a StatusBadge tone. 'low'
 * is the one that matters operationally (it's what widens the cost band), so
 * it gets the warning color; 'high'/'medium' stay quiet (info/neutral).
 */
export function toneForConfidence(confidence: 'high' | 'medium' | 'low'): StatusTone {
  switch (confidence) {
    case 'high': return 'success';
    case 'low': return 'warning';
    default: return 'neutral';
  }
}

/**
 * 2026-07-16 — map a GHEVENT# row's `outcome` (Forge's recordWebhookEvent(),
 * webhook.ts) to a StatusBadge tone. 'dropped_unattached'/'ignored_unhandled'
 * are informational, not failures — the webhook worked as designed, there
 * was just nothing to do. 'error' is the one that needs attention.
 */
export function toneForGitHubEventOutcome(outcome: string): StatusTone {
  switch (outcome) {
    case 'ok': return 'success';
    case 'dropped_unattached':
    case 'ignored_unhandled':
      return 'neutral';
    case 'error': return 'danger';
    default: return 'neutral';
  }
}

/** Friendlier display label for a GHEVENT# row's outcome. */
export function labelForGitHubEventOutcome(outcome: string): string {
  switch (outcome) {
    case 'ok': return 'Handled';
    case 'dropped_unattached': return 'Repo not attached';
    case 'ignored_unhandled': return 'Not actionable';
    case 'error': return 'Error';
    default: return outcome;
  }
}

export default StatusBadge;
